import React, { useState } from "react";
import {
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useIsMobile } from "../../hooks/useIsMobile";

// Type for a generic table item
interface TableItem {
  id: string | number;
  [key: string]: any;
}

interface InfinitePaginationTableProps<T extends TableItem> {
  columns: {
    id: string;
    label: string;
    render?: (item: T) => React.ReactNode;
    /**
     * On phones this column leaves the table and shows inside the row's
     * expandable panel instead, keeping the visible table narrow.
     */
    hideOnMobile?: boolean;
  }[];
  items: T[];
  pageSize?: number;
  loading?: boolean;
  onLoadNext: () => void;
  /** When set, each row gets an expand toggle revealing this content below it. */
  renderExpandedRow?: (item: T) => React.ReactNode;
}

export function InfinitePaginationTable<T extends TableItem>({
  columns,
  items,
  pageSize = 10,
  loading = false,
  onLoadNext,
  renderExpandedRow,
}: InfinitePaginationTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(
    new Set()
  );
  const isMobile = useIsMobile();

  const toggleExpanded = (id: string | number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const visibleColumns = isMobile
    ? columns.filter((column) => !column.hideOnMobile)
    : columns;
  const collapsedColumns = isMobile
    ? columns.filter((column) => column.hideOnMobile)
    : [];
  const expandable =
    renderExpandedRow !== undefined || collapsedColumns.length > 0;

  const columnCount = visibleColumns.length + (expandable ? 1 : 0);

  const totalPages = Math.ceil(items.length / pageSize);
  const showNextPage = items.length >= currentPage * pageSize;

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    if (page === totalPages + 1) {
      onLoadNext();
    }
  };

  const getCurrentPageItems = () => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return items.slice(start, end);
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {expandable && <TableCell sx={{ width: 48 }} />}
              {visibleColumns.map((column) => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {getCurrentPageItems().map((item) => {
              const isExpanded = expandedIds.has(item.id);
              return (
                <React.Fragment key={item.id}>
                  <TableRow>
                    {expandable && (
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          aria-label={isExpanded ? "collapse row" : "expand row"}
                          onClick={() => toggleExpanded(item.id)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    )}
                    {visibleColumns.map((column) => (
                      <TableCell key={`${item.id}-${column.id}`}>
                        {column.render ? column.render(item) : item[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                  {expandable && (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        sx={{
                          py: 0,
                          borderBottom: isExpanded ? undefined : "none",
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          {collapsedColumns.length > 0 && (
                            <Stack gap={1.5} sx={{ py: 1.5, px: 1 }}>
                              {collapsedColumns.map((column) => (
                                <Box key={column.id}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 0.25 }}
                                  >
                                    {column.label}
                                  </Typography>
                                  <Box>
                                    {column.render
                                      ? column.render(item)
                                      : item[column.id]}
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          )}
                          {renderExpandedRow?.(item)}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {loading && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={totalPages + (showNextPage ? 1 : 0)}
          page={currentPage}
          onChange={handlePageChange}
          disabled={loading}
        />
      </Box>
    </Box>
  );
}
